export const CENTS_PER_DOLLAR = BigInt(100);
export const ZERO = BigInt(0);
const TWO = BigInt(2);
const TENTHS = BigInt(10);
const HUNDREDTHS = BigInt(100);

type AmountUnit = { suffix: string; cents: bigint };

const BILLIONS: AmountUnit = { suffix: "B", cents: BigInt(100_000_000_000) };
const MILLIONS: AmountUnit = { suffix: "M", cents: BigInt(100_000_000) };
const THOUSANDS: AmountUnit = { suffix: "K", cents: BigInt(100_000) };

/**
 * K, M, or B when the rounded amount is at least 1.0 of that unit, with at
 * most one decimal and no decimal when it is zero. Smaller amounts stay exact
 * dollars and cents.
 */
const CARD_AMOUNT_UNITS = [BILLIONS, MILLIONS, THOUSANDS] as const;

/** Shared card display for a signed cent total. Zero is `$0.00`. */
export function formatCardAmountFromCents(signedCents: bigint): string {
  const negative = signedCents < ZERO;
  const totalCents = negative ? -signedCents : signedCents;
  const sign = negative ? "-" : "";
  for (const unit of CARD_AMOUNT_UNITS) {
    const tenths = roundedSteps(totalCents, unit, TENTHS);
    if (tenths >= TENTHS) return `${sign}${scaledText(tenths, TENTHS, unit)}`;
  }
  return `${sign}${exactDollarsText(totalCents)}`;
}

const DOLLARS_PER_THOUSAND = THOUSANDS.cents / CENTS_PER_DOLLAR;
const HALF_DOLLAR_CENTS = CENTS_PER_DOLLAR / TWO;

/** 1,000.00 in hundredths of a unit. */
const ONE_THOUSAND_HUNDREDTHS = BigInt(100_000);

/**
 * Compact display for a signed cent total, modeled on the ARR summary table.
 * B from $1,000,000,000, M from $1,000,000 and K from $1,000, rounded half away
 * from zero to two decimals, with no decimals when they are zero. Smaller
 * amounts round half away from zero to whole dollars. Negatives are in
 * parentheses. Zero, and any negative that rounds to zero, is `$0`, never
 * `($0)`. Unlike that table's float formatter, this rounds exact cents, groups
 * thousands, and reads an amount that rounds to 1,000 of a unit in the next one.
 */
export function formatCompactAmountFromCents(signedCents: bigint): string {
  const shownCents = compactAmountRoundsToZero(signedCents) ? ZERO : signedCents;
  return withParentheses(shownCents, compactAmountText);
}

/**
 * Screen-reader text for a negative compact amount, such as "minus $12K".
 * Screen readers do not read the parentheses at their default punctuation level.
 * Undefined when the compact text shows no parentheses.
 */
export function spokenCompactAmountFromCents(signedCents: bigint): string | undefined {
  if (signedCents >= ZERO || compactAmountRoundsToZero(signedCents)) return undefined;
  return `minus ${compactAmountText(-signedCents)}`;
}

function compactAmountRoundsToZero(signedCents: bigint): boolean {
  return roundedWholeDollars(signedCents < ZERO ? -signedCents : signedCents) === ZERO;
}

/** An unsigned cent total in whole dollars, rounded half up. */
function roundedWholeDollars(totalCents: bigint): bigint {
  return (totalCents + HALF_DOLLAR_CENTS) / CENTS_PER_DOLLAR;
}

/** Exact `$1,234.56` for a signed cent total. Negatives are in parentheses. Zero is `$0.00`. */
export function formatExactAmountFromCents(signedCents: bigint): string {
  return withParentheses(signedCents, exactDollarsText);
}

export function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function withParentheses(signedCents: bigint, text: (totalCents: bigint) => string): string {
  return signedCents < ZERO ? `(${text(-signedCents)})` : text(signedCents);
}

function compactAmountText(totalCents: bigint): string {
  const dollars = roundedWholeDollars(totalCents);
  // $999.50 and up rounds to 1,000 dollars, which reads as $1K below.
  if (dollars < DOLLARS_PER_THOUSAND) return `$${dollars.toString()}`;
  // Every amount from $1,000,000 rounds to at least 1,000K, so this one check
  // covers both larger amounts and K amounts that round up to 1,000K. The M
  // check works the same way for B.
  const thousands = roundedSteps(totalCents, THOUSANDS, HUNDREDTHS);
  if (thousands < ONE_THOUSAND_HUNDREDTHS) return scaledText(thousands, HUNDREDTHS, THOUSANDS);
  const millions = roundedSteps(totalCents, MILLIONS, HUNDREDTHS);
  if (millions < ONE_THOUSAND_HUNDREDTHS) return scaledText(millions, HUNDREDTHS, MILLIONS);
  return scaledText(roundedSteps(totalCents, BILLIONS, HUNDREDTHS), HUNDREDTHS, BILLIONS);
}

/** An unsigned cent total in tenths or hundredths of a unit, rounded half up. */
function roundedSteps(totalCents: bigint, unit: AmountUnit, steps: bigint): bigint {
  const stepCents = unit.cents / steps;
  return (totalCents + stepCents / TWO) / stepCents;
}

/**
 * `$1.5M` or `$12.34K` from a rounded step count. `steps` is a power of ten
 * whose zero count sets the decimal places. A zero decimal is omitted.
 */
function scaledText(rounded: bigint, steps: bigint, unit: AmountUnit): string {
  const whole = rounded / steps;
  const fraction = rounded % steps;
  const digits = steps.toString().length - 1;
  const decimal = fraction === ZERO ? "" : `.${fraction.toString().padStart(digits, "0")}`;
  return `$${groupThousands(whole.toString())}${decimal}${unit.suffix}`;
}

/** `$1,234.56` for an unsigned cent total. */
function exactDollarsText(totalCents: bigint): string {
  const dollars = totalCents / CENTS_PER_DOLLAR;
  const fraction = (totalCents % CENTS_PER_DOLLAR).toString().padStart(2, "0");
  return `$${groupThousands(dollars.toString())}.${fraction}`;
}
